import { useState, FormEvent, useRef } from 'react';

const NUISIBLE_OPTIONS = [
  'Rats / souris',
  'Ravets / cafards',
  'Termites',
  'Punaises de lit',
  'Scolopendres',
  'Moustiques',
  'Fourmis',
  'Guêpes',
  'Autre / je ne sais pas',
];

const FORM_SUBMIT_URL = 'https://formspree.io/f/xyegzenq';

interface ContactFormBlockProps {
  source?: string;
}

export default function ContactFormBlock({ source = 'site' }: ContactFormBlockProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const form = e.currentTarget;
    const gotchaEl = form.elements.namedItem('website_alt') as HTMLInputElement | null;
    if (gotchaEl && gotchaEl.value.trim() !== '') {
      setSuccess(true);
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData(form);
      const resp = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      let parsed: { ok?: boolean; errors?: Array<{ message?: string }> } = {};
      try {
        parsed = await resp.json();
      } catch {
        // ignore parse error
      }

      if (!resp.ok || parsed?.ok === false) {
        setError(parsed?.errors?.[0]?.message || 'Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.');
        setSubmitting(false);
        return;
      }

      setSuccess(true);
      form.reset();
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion et réessayez.');
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-500">
            <i className="ri-check-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-3">
            Demande envoyée !
          </h3>
          <p className="text-foreground-600 text-base leading-relaxed">
            Un technicien vous répond sous 2 h ouvrées avec un prix ferme.
          </p>
          <button
            type="button"
            onClick={() => setSuccess(false)}
            className="mt-6 inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Envoyer une autre demande
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-14 md:py-20">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
            Demandez votre devis gratuit
          </h2>
          <p className="text-foreground-600 text-base leading-relaxed max-w-xl mx-auto">
            Décrivez votre problème : un technicien vous répond sous 2 h avec un prix ferme. Pas d&rsquo;engagement.
          </p>
        </div>

        <form
          ref={formRef}
          id="contact-form"
          onSubmit={handleSubmit}
          className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/70"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            {/* Nom */}
            <div>
              <label htmlFor={`nom-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Nom
              </label>
              <input
                type="text"
                id={`nom-${source}`}
                name="nom"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Téléphone */}
            <div>
              <label htmlFor={`telephone-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Téléphone <span className="text-accent-500">*</span>
              </label>
              <input
                type="tel"
                id={`telephone-${source}`}
                name="telephone"
                required
                placeholder="0690 00 00 00"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor={`email-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Email
              </label>
              <input
                type="email"
                id={`email-${source}`}
                name="email"
                placeholder="vous@exemple.com"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Nuisible */}
            <div>
              <label htmlFor={`nuisible-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Quel nuisible ?
              </label>
              <select
                id={`nuisible-${source}`}
                name="nuisible"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              >
                <option value="">— Sélectionnez —</option>
                {NUISIBLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Commune */}
            <div className="md:col-span-2">
              <label htmlFor={`commune-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Votre commune
              </label>
              <input
                type="text"
                id={`commune-${source}`}
                name="commune"
                placeholder="Ex : Les Abymes, Baie-Mahault, Le Gosier..."
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label htmlFor={`description-${source}`} className="block text-foreground-800 text-sm font-semibold mb-2">
                Décrivez le problème
              </label>
              <textarea
                id={`description-${source}`}
                name="description"
                rows={4}
                maxLength={500}
                placeholder="Depuis quand ? Où ? Combien en voyez-vous ?"
                className="w-full px-4 py-3 rounded-lg border border-background-300/70 bg-white text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-500/20 transition-colors resize-none"
              ></textarea>
            </div>
          </div>

          {/* Hidden source */}
          <input type="hidden" name="source" value={source} />

          {/* Anti-spam honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="form-honeypot"
          />

          {/* Erreur */}
          {error && (
            <div className="mt-5 p-4 rounded-lg bg-accent-100 text-accent-900 text-sm border border-accent-300">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full py-3.5 rounded-lg bg-accent-500 text-white font-heading font-bold text-base hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Envoi en cours...' : 'Recevoir mon prix ferme'}
          </button>

          <p className="text-foreground-400 text-xs text-center mt-4 leading-relaxed">
            Réponse sous 2 h ouvrées. Vos données ne servent qu&rsquo;à vous répondre — jamais revendues.
          </p>
        </form>
      </div>
    </section>
  );
}